import { useRestoreSessionMutation } from '../apis';
import { useAuthenticationFlow } from '../contexts';
import { ConfigureServices } from '@invision/core';
import { useHistory } from 'react-router';
import { AuthenticationStorage } from '../utils';
import { usePostAuthRedirect } from './usePostAuthRedirect';
import { useUserPreferences } from './useStorageState';

/** External sign in flow a callback belongs to, used to label its log entries. */
export type ExternalLoginContext = 'idp' | 'oauth';

/**
 * Restores an Ascendon session from an external provider token. Failures
 * propagate to the calling callback page, which owns logging and the
 * user-facing error through a single catch path — no parallel error state.
 */
export function useExternalCallbacks(logContext: ExternalLoginContext) {
  const { mutateAsync: restoreSession } = useRestoreSessionMutation();
  const { redirectUrl, setSessionId, setSystemId, setRedirectUrl } =
    useAuthenticationFlow();
  const { selectedEnvironment } = useUserPreferences();
  const history = useHistory();
  const performPostAuthRedirect = usePostAuthRedirect();

  return {
    handleExternalLoginSuccess: async function handleExternalLoginSuccess(
      decodedToken: string,
      systemId?: string,
      accessTokenPrefix?: string | null
    ) {
      // Restore session using the standard restore session endpoint
      const sessionResponse = await restoreSession({
        request: {
          Token: accessTokenPrefix
            ? `${accessTokenPrefix}_${decodedToken}`
            : `${decodedToken}`
        },
        systemId: systemId
      });

      // Store session data from response
      const { SessionId, SystemId, SessionSummary } = sessionResponse;

      // Validate that restored session matches expected system for security
      if (SystemId !== systemId) {
        throw new Error('System ID mismatch - security validation failed');
      }

      if (SessionId) setSessionId(SessionId);
      if (SystemId) setSystemId(SystemId);

      // Stick this tab to React auth after a confirmed external (IDP/OAuth)
      // restore, including environments that are not config-enabled.
      // TODO(AB#1355453): Remove with the auth-mode opt-in once React auth is
      // the default in every environment.
      if (SessionId && SystemId) {
        AuthenticationStorage.setReactAuthMode();
      }

      // Configure API services with restored session data
      if (SessionId && SystemId && selectedEnvironment) {
        ConfigureServices({
          apiKey: '',
          environment: {
            name: selectedEnvironment,
            value: selectedEnvironment
          },
          preferences: {},
          sessionId: SessionId,
          systemId: SystemId
        });
      }

      await performPostAuthRedirect({
        sessionSummary: SessionSummary,
        systemId: SystemId ?? null,
        storedRedirectUrl: redirectUrl,
        clearStoredRedirectUrl: () => setRedirectUrl(null),
        history,
        logContext,
        addDelay: true
      });
    }
  };
}
